import ComputerDesktopIcon from "@heroicons/react/24/solid/ComputerDesktopIcon";
import DeviceTabletIcon from "@heroicons/react/24/solid/DeviceTabletIcon";
import PhoneIcon from "@heroicons/react/24/solid/PhoneIcon";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import React from "react";

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import ApexChart from "react-apexcharts";
import { styled } from "@mui/material/styles";
import SigneeRegisterInfo from "../popover/SigneeRegisterInfo";
import { shallowEqual, useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
const Chart = styled(ApexChart)``;

const getChartOptions = (theme, labels, isUnlimitedTier, role, admin) => {
  return {
    chart: {
      background: "transparent",
    },
    colors: [theme.palette.primary.main, theme.palette.error.main],
    dataLabels: {
      enabled: false,
    },
    labels,
    legend: {
      show: false,
    },
    plotOptions: {
      pie: {
        expandOnClick: false,
      },
    },
    states: {
      active: {
        filter: {
          type: "none",
        },
      },
      hover: {
        filter: {
          type: "none",
        },
      },
    },
    stroke: {
      width: 0,
    },
    theme: {
      mode: theme.palette.mode,
    },
    tooltip: {
      y: {
        formatter: function (value, { seriesIndex, dataPointIndex, w }) {
          if (
            isUnlimitedTier ||
            role === "admin" ||
            role === "tester" ||
            admin === true
          ) {
            // Adjust this condition as needed
            return "Unlimited";
          }
          return value;
        },
      },
      fillSeriesColor: false,
    },
  };
};

const iconMap = {
  Desktop: (
    <SvgIcon>
      <ComputerDesktopIcon />
    </SvgIcon>
  ),
  Tablet: (
    <SvgIcon>
      <DeviceTabletIcon />
    </SvgIcon>
  ),
  Phone: (
    <SvgIcon>
      <PhoneIcon />
    </SvgIcon>
  ),
};

const AvailableNoc = () => {
  const theme = useTheme();
  const labels = useMemo(() => ["Available", "Used"], []);
  const currentSession = useSelector((state) => state.sessions.currentSession);

  const { role: uRole, admin: uAdmin } = useSelector(
    (state) => state[currentSession]?.user?.user || {},
    shallowEqual
  );

  const { noc: uTierNoc } = useSelector(
    (state) => state[currentSession]?.user?.tier || {},
    shallowEqual
  );

  const {
    nocCreatedInPeriod: uAnalyticsNocCreated,
    availableNocInPeriod: uAnalyticsAvailableNoc,
  } = useSelector(
    (state) => state[currentSession]?.user?.user?.analytics || {},
    shallowEqual
  );

  const [availableTitle, setAvailableTitle] = useState(false);
  const [isAvailableFadingOut, setIsAvailableFadingOut] = useState(false);

  const between1200and1405 = useMediaQuery(
    "(min-width:1200px) and (max-width:1405px)"
  );
  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const chartOptions = useMemo(
    () =>
      getChartOptions(
        theme,
        labels,
        parseInt(uTierNoc) === "unlimited",
        uRole,
        uAdmin
      ),
    [theme, labels, uAdmin, uRole, uTierNoc]
  );

  useEffect(() => {
    if (availableTitle) {
      const availableTimeout = setTimeout(() => {
        setIsAvailableFadingOut(true);
        const availableFadeOutTimeout = setTimeout(() => {
          setAvailableTitle(false);
          setIsAvailableFadingOut(false);
        }, 1000);

        return () => clearTimeout(availableFadeOutTimeout);
      }, 3500);

      return () => clearTimeout(availableTimeout);
    }
  }, [availableTitle]);

  const chartSeries = useMemo(
    () =>
      uRole === "admin" || uRole === "tester" || uAdmin === true
        ? [100000000000000, parseInt(uAnalyticsNocCreated)]
        : [
            uAnalyticsAvailableNoc === "unlimited"
              ? 100000000000000
              : parseInt(uAnalyticsAvailableNoc) -
                parseInt(uAnalyticsNocCreated),
            parseInt(uAnalyticsNocCreated),
          ],
    [uAdmin, uAnalyticsAvailableNoc, uAnalyticsNocCreated, uRole]
  );

  const title =
    "This pie chart illustrates the distribution of available NOCs within the current 30-day period. Any NOCs created during this period will impact the available count for all users in the organization.";
  return (
    <Card sx={{ position: "relative" }}>
      {tabletSize ? (
        <IconButton
          onClick={() => setAvailableTitle(!availableTitle)}
          sx={{ position: "absolute", right: 0, top: 0 }}
        >
          <HelpOutlineIcon color="primary" />
        </IconButton>
      ) : (
        <Tooltip title={title} placement="top">
          <HelpOutlineIcon
            color="primary"
            sx={{ position: "absolute", right: 10, top: 10 }}
          />
        </Tooltip>
      )}

      {availableTitle && (
        <SigneeRegisterInfo
          infoText={title}
          isFadingOut={isAvailableFadingOut}
          top={35}
        />
      )}

      <CardHeader
        title={
          <>
            <Typography variant="h6">Remaining Free</Typography>
            <Typography variant="h5" sx={{ mt: 0, pt: 0 }}>
              Notice of Commencement Forms
            </Typography>
          </>
        }
        sx={{ textAlign: "center" }} // Optional: Center the text
      />
      <CardContent
        sx={{
          px: tabletSize ? 1.5 : between1200and1405 ? 1 : 0,
          pt: mobileSize ? 1.5 : tabletSize ? 3 : undefined,
          pb: tabletSize ? 1.5 : undefined,
        }}
      >
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="center"
          spacing={2}
        >
          {chartSeries.map((item, index) => {
            const label = labels[index];

            return (
              <Box
                key={label}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  pb: 2,
                }}
              >
                {iconMap[label]}
                <Typography
                  sx={
                    label === "Available"
                      ? { my: 1, color: "#004976" }
                      : { my: 1, color: "#F04438" }
                  }
                  variant="subtitle1"
                >
                  {label}
                </Typography>
                <Typography
                  sx={
                    label === "Available"
                      ? { color: "#004976" }
                      : { color: "#F04438" }
                  }
                  variant="h6"
                >
                  {label === "Available" &&
                  (uTierNoc === "unlimited" ||
                    uRole === "admin" ||
                    uRole === "tester" ||
                    uAdmin === true)
                    ? "Unlimited"
                    : item}
                </Typography>
              </Box>
            );
          })}
        </Stack>
        <Chart
          height={mobileSize ? 200 : tabletSize ? 250 : 300}
          options={chartOptions}
          series={chartSeries}
          type="donut"
          width="100%"
        />
      </CardContent>
    </Card>
  );
};

export default React.memo(AvailableNoc);
